/**
 * The venue library (spec §8.6). Venues are global — created once, attached to
 * any tournament from its Event details, and picked per game when scheduling.
 * Each venue carries its playing fields, which the public tournament page
 * lists as the possible locations for that event.
 */
import { useState } from "react";
import { api, apiErrorMessage } from "../../lib/api";
import { useAdminVenues, useAdminVenueMutation } from "../../lib/adminData";
import type { Venue } from "../../lib/types";
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  MapPinIcon,
  PageHeader,
  PlusIcon,
  SectionCard,
  Skeleton,
  stagger,
  TrashIcon,
  Workbench,
  XIcon,
} from "../../components/ui";

interface VenueDraft {
  name: string;
  address: string;
  mapsUrl: string;
  fields: { name: string; notes: string }[];
}

const emptyDraft = (): VenueDraft => ({ name: "", address: "", mapsUrl: "", fields: [] });

const toDraft = (v: Venue): VenueDraft => ({
  name: v.name,
  address: v.address,
  mapsUrl: v.mapsUrl ?? "",
  fields: v.fields.map((f) => ({ name: f.name, notes: f.notes ?? "" })),
});

/** Draft -> request body, dropping the optional strings the API won't accept empty. */
function toBody(d: VenueDraft) {
  return {
    name: d.name.trim(),
    address: d.address.trim(),
    mapsUrl: d.mapsUrl.trim() || undefined,
    fields: d.fields
      .filter((f) => f.name.trim())
      .map((f) => ({ name: f.name.trim(), notes: f.notes.trim() || undefined })),
  };
}

/** Shared by the add form and each venue's edit form. */
function VenueForm({
  draft,
  setDraft,
  submitLabel,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  draft: VenueDraft;
  setDraft: (next: VenueDraft) => void;
  submitLabel: string;
  pending: boolean;
  error?: string;
  onSubmit: () => void;
  onCancel?: () => void;
}) {
  const setField = (i: number, patch: Partial<{ name: string; notes: string }>) =>
    setDraft({
      ...draft,
      fields: draft.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)),
    });

  const valid = draft.name.trim() && draft.address.trim();

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onSubmit();
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Venue name">
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Riverside Sports Complex"
            maxLength={120}
            required
          />
        </Field>
        <Field label="Address">
          <Input
            value={draft.address}
            onChange={(e) => setDraft({ ...draft, address: e.target.value })}
            placeholder="1200 River Rd, Dallas, TX 75201"
            maxLength={240}
            required
          />
        </Field>
        <Field
          label="Maps link"
          className="sm:col-span-2"
          hint="Optional. Leave blank to link a map search for the address."
        >
          <Input
            type="url"
            value={draft.mapsUrl}
            onChange={(e) => setDraft({ ...draft, mapsUrl: e.target.value })}
            placeholder="https://maps.app.goo.gl/…"
          />
        </Field>
      </div>

      <div className="space-y-2">
        <span className="block text-sm font-medium text-slate-600">Fields</span>
        {draft.fields.length === 0 ? (
          <p className="text-sm text-slate-400">
            No fields yet. Add the diamonds teams will play on at this venue.
          </p>
        ) : (
          <ul className="space-y-2">
            {draft.fields.map((f, i) => (
              <li key={i} className="flex items-center gap-2">
                <Input
                  value={f.name}
                  onChange={(e) => setField(i, { name: e.target.value })}
                  placeholder="Field 1"
                  maxLength={60}
                  className="sm:w-40"
                  aria-label={`Field ${i + 1} name`}
                />
                <Input
                  value={f.notes}
                  onChange={(e) => setField(i, { notes: e.target.value })}
                  placeholder="Turf, lights (optional)"
                  maxLength={200}
                  aria-label={`Field ${i + 1} notes`}
                />
                <button
                  type="button"
                  onClick={() =>
                    setDraft({ ...draft, fields: draft.fields.filter((_, j) => j !== i) })
                  }
                  aria-label={`Remove field ${f.name || i + 1}`}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setDraft({ ...draft, fields: [...draft.fields, { name: "", notes: "" }] })}
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Add field
        </Button>
      </div>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <div className="flex gap-2">
        <Button type="submit" disabled={!valid || pending}>
          {pending ? "Saving…" : submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </form>
  );
}

function VenueRow({ venue }: { venue: Venue }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => toDraft(venue));
  const [error, setError] = useState<string>();

  const save = useAdminVenueMutation(() => api.put(`/admin/venues/${venue.id}`, toBody(draft)));
  const remove = useAdminVenueMutation(() => api.del(`/admin/venues/${venue.id}`));

  const startEdit = () => {
    setDraft(toDraft(venue));
    setError(undefined);
    setEditing(true);
  };

  if (editing) {
    return (
      <Card className="p-5">
        <VenueForm
          draft={draft}
          setDraft={setDraft}
          submitLabel="Save venue"
          pending={save.isPending}
          error={error}
          onCancel={() => setEditing(false)}
          onSubmit={() =>
            save.mutate(undefined, {
              onSuccess: () => setEditing(false),
              onError: (e) => setError(apiErrorMessage(e, "Could not save this venue.")),
            })
          }
        />
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brandText">
            <MapPinIcon className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="font-medium text-navy-950">{venue.name}</p>
            <p className="text-sm text-slate-500">{venue.address}</p>
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          <Button variant="ghost" size="sm" onClick={startEdit}>
            Edit
          </Button>
          <button
            type="button"
            onClick={() => {
              setError(undefined);
              remove.mutate(undefined, {
                onError: (e) => setError(apiErrorMessage(e, "Could not delete this venue.")),
              });
            }}
            disabled={remove.isPending}
            aria-label={`Delete ${venue.name}`}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:opacity-50"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {venue.fields.length > 0 ? (
        <ul className="mt-3 flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
          {venue.fields.map((f, i) => (
            <li
              key={i}
              className="inline-flex items-baseline gap-1.5 rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
            >
              {f.name}
              {f.notes ? <span className="text-slate-400">{f.notes}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
    </Card>
  );
}

export function AdminVenues() {
  const venuesQ = useAdminVenues();
  const [draft, setDraft] = useState(emptyDraft);
  const [error, setError] = useState<string>();

  const create = useAdminVenueMutation(() => api.post("/admin/venues", toBody(draft)));
  const venues = venuesQ.data?.venues ?? [];

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin"
        title="Venues"
        subtitle="Ballparks and their fields, shared across every tournament."
      />

      <Workbench
        aside={
          <SectionCard title="Add a venue" className="animate-rise" style={stagger(2)}>
            <VenueForm
              draft={draft}
              setDraft={setDraft}
              submitLabel="Add venue"
              pending={create.isPending}
              error={error}
              onSubmit={() =>
                create.mutate(undefined, {
                  onSuccess: () => {
                    setDraft(emptyDraft());
                    setError(undefined);
                  },
                  onError: (e) => setError(apiErrorMessage(e, "Could not add this venue.")),
                })
              }
            />
          </SectionCard>
        }
      >
        <div className="animate-rise space-y-3" style={stagger(1)}>
          {venuesQ.isLoading ? (
            <>
              <Skeleton className="h-20 rounded-2xl" />
              <Skeleton className="h-20 rounded-2xl" />
            </>
          ) : venues.length === 0 ? (
            <EmptyState
              icon={<MapPinIcon className="h-5 w-5" />}
              title="No venues yet"
              hint="Add a ballpark under “Add a venue”, then attach it to a tournament from its Event details."
            />
          ) : (
            venues.map((v) => <VenueRow key={v.id} venue={v} />)
          )}
        </div>
      </Workbench>
    </div>
  );
}
